import { motion } from "framer-motion";
import { Clock, Building2, Monitor, Landmark, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { BREAKFAST_ITEMS, LUNCH_ITEMS, SWEET_ITEMS, IMG } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const PERFECT_ICONS = [Building2, Monitor, Landmark, Users];

const CONFIG = {
  breakfast: { items: BREAKFAST_ITEMS, hero: IMG.catBreakfast, layout: "rows" },
  lunch: { items: LUNCH_ITEMS, hero: IMG.catLunch, layout: "cards" },
  sweets: { items: SWEET_ITEMS, hero: IMG.spices, layout: "rows" },
};

const Reveal = ({ children, delay = 0, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

const Row = ({ item, lang, testId }) => (
  <li className="flex items-center gap-5 border-b border-black/5 py-5" data-testid={testId}>
    {item.img && (
      <img
        src={item.img}
        alt={item.name[lang]}
        loading="lazy"
        className="h-16 w-16 shrink-0 rounded-full border-2 border-brand/40 object-cover"
      />
    )}
    <div className="min-w-0 flex-1">
      <p className="font-display text-base font-semibold text-navy">{item.name[lang]}</p>
      <p className="mt-0.5 truncate text-sm text-ink/50 sm:whitespace-normal">{item.desc[lang]}</p>
    </div>
    <span className="shrink-0 font-display text-base font-bold text-branddark">{item.price}</span>
  </li>
);

const LunchCard = ({ item, lang, testId }) => (
  <div
    className="group flex overflow-hidden rounded-2xl bg-paper shadow-sm transition-shadow duration-300 hover:shadow-lg"
    data-testid={testId}
  >
    <img
      src={item.img}
      alt={item.name[lang]}
      loading="lazy"
      className="w-28 shrink-0 object-cover transition-transform duration-500 group-hover:scale-105 sm:w-36"
    />
    <div className="flex flex-1 flex-col p-5">
      <p className="font-display text-base font-semibold text-navy">{item.name[lang]}</p>
      <p className="mt-1 text-sm text-ink/50">{item.desc[lang]}</p>
      <p className="mt-auto pt-3 font-display text-base font-bold text-branddark">{item.price}</p>
    </div>
  </div>
);

export default function ServiceMenuPage({ t, lang, kind }) {
  const { items, hero, layout } = CONFIG[kind];
  const meta = t[`${kind}Page`];

  return (
    <main data-testid={`${kind}-page`}>
      <Seo
        title={`${meta.title} — Kurkuma AI Kitchen`}
        siteName="Kurkuma AI Kitchen"
        description={meta.sub}
      />

      {/* page hero band */}
      <section className="relative overflow-hidden bg-navy text-cream" data-testid={`${kind}-hero`}>
        <img src={hero} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-4 px-4 py-16 md:flex-row md:items-end md:justify-between md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl" data-testid={`${kind}-heading`}>
              {meta.title}
            </h1>
            <p className="mt-2 text-cream/70">{meta.sub}</p>
          </motion.div>
          {meta.hours && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="inline-flex w-fit items-center gap-2 rounded-full bg-brand px-5 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy"
              data-testid={`${kind}-hours`}
            >
              <Clock className="h-3.5 w-3.5" /> {meta.hours}
            </motion.span>
          )}
          {meta.note && (
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cream/60">{meta.note}</span>
          )}
        </div>
      </section>

      {/* items */}
      <section className="mx-auto max-w-5xl px-4 py-14 md:px-8" data-testid={`${kind}-menu`}>
        {layout === "rows" ? (
          <Reveal>
            <ul data-testid={`${kind}-list`}>
              {items.map((item) => (
                <Row key={item.id} item={item} lang={lang} testId={`${kind}-item-${item.id}`} />
              ))}
            </ul>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {items.map((item, i) => (
              <Reveal key={item.id} delay={(i % 2) * 0.08}>
                <LunchCard item={item} lang={lang} testId={`${kind}-item-${item.id}`} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* lunch extras: how it works + perfect for */}
      {kind === "lunch" && (
        <>
          <section className="bg-paper py-14" data-testid="lunch-how-it-works">
            <div className="mx-auto max-w-7xl px-4 md:px-8">
              <Reveal className="mb-10">
                <h2 className="font-display text-3xl font-bold text-navy">{t.lunchPage.howTitle}</h2>
              </Reveal>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {t.lunchPage.steps.map((s, i) => (
                  <Reveal key={s.t} delay={i * 0.08} data-testid={`how-step-${i}`}>
                    <span className="font-display text-4xl font-extrabold text-brand">{i + 1}</span>
                    <p className="mt-2 font-display text-base font-semibold text-navy">{s.t}</p>
                    <p className="mt-1 text-sm text-ink/50">{s.d}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
          <section className="bg-navy py-12 text-cream" data-testid="lunch-perfect-for">
            <div className="mx-auto max-w-7xl px-4 md:px-8">
              <Reveal className="mb-8">
                <h2 className="font-display text-2xl font-bold">{t.lunchPage.perfectTitle}</h2>
              </Reveal>
              <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
                {t.lunchPage.perfect.map((p, i) => {
                  const Icon = PERFECT_ICONS[i];
                  return (
                    <Reveal key={p} delay={i * 0.06} className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/15 text-brand">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-sm font-medium text-cream/80">{p}</span>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
